import React from 'react';
import Select from '../../../components/ui/Select';
import { cn } from '../../../utils/cn';

const RolePermissionsSection = ({
  formData,
  errors,
  onFieldChange
}) => {
  const roles = [
    {
      value: 'admin',
      label: 'Administrador',
      description: 'Acesso total ao sistema, incluindo gestão de usuários e configuração',
      permissions: [
        'Criar, editar e excluir contas de usuários',
        'Gerenciar configurações do sistema', 
        'Acesso total a dados e operações dos membros',
        'Ver logs e analytics do sistema',
        'Exportar e importar dados'
      ],
      color: 'red',
      icon: '👑'
    },
    {
      value: 'manager',
      label: 'Gestor',
      description: 'Gestão de membros e capacidades de relatórios',
      permissions: [
        'Visualizar e gerenciar perfis de membros',
        'Criar e editar informações de membros',
        'Gerar relatórios e análises',
        'Exportar dados de membros',
        'Gerenciar importações e operações em massa'
      ],
      color: 'orange',
      icon: '👥'
    },
    {
      value: 'viewer',
      label: 'Leitor',
      description: 'Acesso somente leitura a dados de membros e relatórios básicos',
      permissions: [
        'Ver perfis e informações dos membros',
        'Acessar relatórios e estatísticas básicas',
        'Buscar e filtrar dados de membros',
        'Ver logs de atividades',
        'Exportar listas básicas de membros'
      ],
      color: 'green',
      icon: '👀'
    }
  ];

  const selectedRole = roles?.find(role => role?.value === formData?.role);

  return (
    <div className="bg-white rounded-lg border border-border shadow-sm p-6">
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-foreground mb-2">
          Perfil e Permissões
        </h2>
        <p className="text-sm text-muted-foreground">
          Selecione o perfil adequado e revise as permissões de acesso
        </p>
      </div>
      {/* Role Selection */}
      <div className="mb-6">
        <label htmlFor="role" className="block text-sm font-medium text-foreground mb-2">
          Perfil do usuário <span className="text-red-500">*</span>
        </label>
        <Select
          value={formData?.role}
          onValueChange={(value) => onFieldChange?.('role', value)}
          placeholder="Selecione o perfil do usuário..."
          className={cn(
            errors?.role && 'border-red-300 focus:border-red-500'
          )}
        >
          {roles?.map((role) => (
            <Select.Item key={role?.value} value={role?.value}>
              <div className="flex items-center space-x-2">
                <span>{role?.icon}</span>
                <span>{role?.label}</span>
              </div>
            </Select.Item>
          ))}
        </Select>
        {errors?.role && (
          <p className="mt-1 text-xs text-red-600">
            {errors?.role}
          </p>
        )}
      </div>
      {/* Role Details */}
      {selectedRole && (
        <div className={cn(
          'border rounded-lg p-4 mb-4',
          {
            'border-red-200 bg-red-50': selectedRole?.color === 'red',
            'border-orange-200 bg-orange-50': selectedRole?.color === 'orange',
            'border-green-200 bg-green-50': selectedRole?.color === 'green'
          }
        )}>
          <div className="flex items-start space-x-3">
            <span className="text-2xl">{selectedRole?.icon}</span>
            <div className="flex-1">
              <h3 className={cn(
                'font-semibold text-base mb-1',
                {
                  'text-red-800': selectedRole?.color === 'red',
                  'text-orange-800': selectedRole?.color === 'orange',
                  'text-green-800': selectedRole?.color === 'green'
                }
              )}>
                {selectedRole?.label}
              </h3>
              <p className={cn(
                'text-sm mb-3',
                {
                  'text-red-700': selectedRole?.color === 'red',
                  'text-orange-700': selectedRole?.color === 'orange',
                  'text-green-700': selectedRole?.color === 'green'
                }
              )}>
                {selectedRole?.description}
              </p>

              <div>
                <h4 className={cn(
                  'font-medium text-sm mb-2',
                  {
                    'text-red-800': selectedRole?.color === 'red',
                    'text-orange-800': selectedRole?.color === 'orange',
                    'text-green-800': selectedRole?.color === 'green'
                  }
                )}>
                  Permissões incluem:
                </h4>
                <ul className="space-y-1">
                  {selectedRole?.permissions?.map((permission, index) => (
                    <li
                      key={index}
                      className={cn(
                        'text-xs flex items-start space-x-2',
                        {
                          'text-red-700': selectedRole?.color === 'red',
                          'text-orange-700': selectedRole?.color === 'orange',
                          'text-green-700': selectedRole?.color === 'green'
                        }
                      )}
                    >
                      <svg className="w-3 h-3 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span>{permission}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Role Comparison */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
        <div className="flex items-start space-x-2">
          <svg className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">Diretrizes de atribuição de perfis</p>
            <ul className="list-disc list-inside space-y-0.5 text-xs">
              <li><strong>Administrador:</strong> Para gestores do sistema e equipe técnica</li>
              <li><strong>Gestor:</strong> Para coordenadores e gestores de dados</li>
              <li><strong>Leitor:</strong> Para analistas e usuários com acesso de leitura</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RolePermissionsSection;